import { useState, useEffect, useCallback, useRef } from 'react';
import websocket from '../services/websocket';

/**
 * Manages WAITER_CALL and BILL_REQUEST alerts per table.
 * Returns alerts keyed by tableNumber string, e.g.:
 * { "3": { waiterCall: true, billRequest: true, timestamp: "..." } }
 */
export const useWaiterNotifications = (enabled) => {
  const [alerts, setAlerts] = useState({});
  // eslint-disable-next-line no-unused-vars
  const audioRef = useRef(null);

  const playAlertSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      // Two-tone alert: 880Hz + 1100Hz
      [880, 1100].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.value = 0.3;
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3 + i * 0.15);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + i * 0.15);
        osc.stop(ctx.currentTime + 0.4 + i * 0.15);
      });
    } catch (e) {
      console.warn('Could not play alert sound:', e);
    }
  }, []);

  const handleWaiterCall = useCallback(
    (data) => {
      const tableNum = String(data.data?.tableNumber || data.tableNumber);
      if (!tableNum) return;

      setAlerts((prev) => ({
        ...prev,
        [tableNum]: {
          ...prev[tableNum],
          waiterCall: true,
          waiterCallTime: data.data?.timestamp || new Date().toISOString(),
        },
      }));
      playAlertSound();
    },
    [playAlertSound]
  );

  const handleBillRequest = useCallback(
    (data) => {
      const tableNum = String(data.data?.tableNumber || data.tableNumber);
      if (!tableNum) return;

      setAlerts((prev) => ({
        ...prev,
        [tableNum]: {
          ...prev[tableNum],
          billRequest: true,
          billRequestTime: data.data?.timestamp || new Date().toISOString(),
        },
      }));
      playAlertSound();
    },
    [playAlertSound]
  );

  useEffect(() => {
    if (!enabled) return;

    websocket.on('WAITER_CALL', handleWaiterCall);
    websocket.on('BILL_REQUEST', handleBillRequest);

    return () => {
      websocket.off('WAITER_CALL', handleWaiterCall);
      websocket.off('BILL_REQUEST', handleBillRequest);
    };
  }, [enabled, handleWaiterCall, handleBillRequest]);

  const clearAlert = useCallback((tableNumber) => {
    setAlerts((prev) => {
      const next = { ...prev };
      delete next[String(tableNumber)];
      return next;
    });
  }, []);

  return { alerts, clearAlert };
};
